/**
 * DateTime Picker Component
 */

import { v4 as uuidv4 } from 'uuid'

import { IBuilderComponent } from '../../base'
import { formatFromVariableSelect, formatFromVariableText } from '../../format-utils'
import type { VariableSelectValue, VariableTextValue } from '../../types'
import { deepMerge, parseToVariableText } from '../../parser-utils'
import datetimePickerTemplate from './datetime-picker.template.json'

export interface IDateTimePickerComponentSettings {
  // Data settings
  'datetime-picker-data-value': string // Column ID
  'datetime-picker-data-label': VariableTextValue
  'datetime-picker-data-default-value': VariableSelectValue
  'datetime-picker-data-default-value-editable': boolean

  // Behavior settings
  'datetime-picker-behavior-start-time': string
  'datetime-picker-behavior-end-time': string
  'datetime-picker-behavior-time-interval': string
  'datetime-picker-behavior-days-allowed': number[]
}

export interface IDateTimePickerComponent extends IBuilderComponent {
  componentId: 'datetime-picker'
  groupId: 'form'
  settings: IDateTimePickerComponentSettings
}

/**
 * DateTime Picker AI Input
 */
export interface AiDateTimePickerInput {
  columnId: string // Required: maps to table column
  label?: string[] // Format: ["text:Select Date"] or ["text:Choose ", "cr:dateType"]
  defaultValue?: string // Default value in select format: "dt:today" or "dt:now" or ISO date string
  startTime?: string // Start time (e.g., "09:00")
  endTime?: string // End time (e.g., "17:00")
  timeInterval?: number // Time interval in minutes
  daysAllowed?: number[] // Days of week allowed (0 = Sunday)
  required?: boolean // Is this field required?
}

const DATETIME_PICKER_DEFAULT_TEMPLATE =
  datetimePickerTemplate as unknown as IDateTimePickerComponentSettings

export class DateTimePickerComp {
  getTemplate(): IDateTimePickerComponentSettings {
    return DATETIME_PICKER_DEFAULT_TEMPLATE
  }

  /**
   * format datetime picker component settings to AI datetime picker input
   */
  format(settings: IDateTimePickerComponentSettings): AiDateTimePickerInput {
    const template = this.getTemplate()
    const result: AiDateTimePickerInput = {
      columnId: settings['datetime-picker-data-value'] || '',
    }

    // Label
    const label = formatFromVariableText(settings['datetime-picker-data-label'])
    if (label && label.length > 0) {
      result.label = label
    }

    // Default Value
    const defaultValue = formatFromVariableSelect(settings['datetime-picker-data-default-value'])
    if (defaultValue) {
      result.defaultValue = defaultValue
    }

    // Start Time
    if (settings['datetime-picker-behavior-start-time']) {
      result.startTime = settings['datetime-picker-behavior-start-time']
    }

    // End Time
    if (settings['datetime-picker-behavior-end-time']) {
      result.endTime = settings['datetime-picker-behavior-end-time']
    }

    // Time Interval
    if (settings['datetime-picker-behavior-time-interval']) {
      result.timeInterval = parseInt(settings['datetime-picker-behavior-time-interval'], 10)
    }

    // Days Allowed
    if (settings['datetime-picker-behavior-days-allowed'] && settings['datetime-picker-behavior-days-allowed'].length > 0) {
      result.daysAllowed = settings['datetime-picker-behavior-days-allowed']
    }

    return result
  }

  /**
   * parse AI datetime picker input to datetime picker component settings
   */
  parse(input: AiDateTimePickerInput): IDateTimePickerComponentSettings {
    const result = deepMerge(DATETIME_PICKER_DEFAULT_TEMPLATE, {})

    // Data - Column ID (required for form fields)
    if (input.columnId) {
      result['datetime-picker-data-value'] = input.columnId
    }

    // Data - Label
    if (input.label) {
      result['datetime-picker-data-label'] = parseToVariableText(input.label)
    }

    // Data - Default Value
    if (input.defaultValue !== undefined) {
      result['datetime-picker-data-default-value'] = {
        flag: 131072, // CurrentDate flag
        id: uuidv4(),
      }
    }

    // Behavior - Time Range
    if (input.startTime) {
      result['datetime-picker-behavior-start-time'] = input.startTime
    }

    if (input.endTime) {
      result['datetime-picker-behavior-end-time'] = input.endTime
    }

    // Behavior - Time Interval
    if (input.timeInterval !== undefined) {
      result['datetime-picker-behavior-time-interval'] = input.timeInterval.toString()
    }

    // Behavior - Days Allowed
    if (input.daysAllowed) {
      result['datetime-picker-behavior-days-allowed'] = input.daysAllowed
    }

    return result
  }
}

